// components/KnowledgeHub.js
import blogSlugs from '../data/blogSlugs';
import projectSlugs from '../data/projectSlugs';
import podcastSlugs from '../data/podcastSlugs';
import { hashString } from '../utils/hashString';
import { pickOneBySeed } from '../utils/pickBySeed';
import Link from 'next/link';

export default function KnowledgeHub({ currentPath, serviceSlug }) {
  const baseSeed = hashString(currentPath);

  /* =========================================
     STRICT PRIMARY TOPIC DETECTION
  ========================================= */
  const getPrimaryTopic = (slug) => {
    if (!slug) return null;
    slug = slug.toLowerCase();
    if (slug.includes('kitchen')) return 'kitchen';
    if (slug.includes('bathroom')) return 'bathroom';
    if (slug.includes('roof')) return 'roof';
    if (slug.includes('adu')) return 'adu';
    if (slug.includes('outdoor')) return 'outdoor';
    if (slug.includes('paver')) return 'paver';
    if (slug.includes('paint')) return 'paint';
    if (slug.includes('window')) return 'window';
    if (slug.includes('solar')) return 'solar';
    if (slug.includes('hvac')) return 'hvac';
    if (slug.includes('gutter')) return 'gutter';
    if (slug.includes('foundation')) return 'foundation';
    if (slug.includes('artificial-turf') || slug.includes('turf')) return 'artificial-turf';
    if (slug.includes('home')) return 'home'; // covers complete-home-remodeling
    return null;
  };

  const primaryTopic = getPrimaryTopic(serviceSlug);

  /* =========================================
     PROJECT SELECTION
  ========================================= */
  const relatedProjects = primaryTopic
    ? projectSlugs.filter(slug => slug.toLowerCase().includes(primaryTopic))
    : [];

  const projectPool = relatedProjects.length > 0 ? relatedProjects : projectSlugs;
  const project = pickOneBySeed(projectPool, baseSeed + 1);

  /* =========================================
     BLOG SELECTION
  ========================================= */
  const relatedBlogs = primaryTopic
    ? blogSlugs.filter(slug => slug.toLowerCase().includes(primaryTopic))
    : [];

  const blogPool = relatedBlogs.length > 0 ? relatedBlogs : blogSlugs;
  const blog = pickOneBySeed(blogPool, baseSeed);

  /* =========================================
     PODCAST SELECTION
  ========================================= */
  const podcast = podcastSlugs.length > 0 ? pickOneBySeed(podcastSlugs, baseSeed + 2) : null;

  /* =========================================
     SERVICE ROTATION (TOPIC FILTERED)
  ========================================= */
  const services = [
    'asphalt-shingles', 'tile-roof', 'flat-roof', 'metal-roof', 'tpo-roof',
    'gutter', 'kitchen-remodeling', 'bathroom-remodeling', 'complete-home-remodeling',
    'exterior-painting', 'earthquake-retrofit', 'adu', 'foundations', 'pavers',
    'outdoor-living', 'energy-efficient-windows', 'solar-panels', 'artificial-turf', 'hvac'
  ];

  let relatedServices = services.filter(s => s !== serviceSlug);
  if (primaryTopic) {
    const strongServices = relatedServices.filter(s => s.includes(primaryTopic));
    if (strongServices.length > 0) relatedServices = strongServices;
  }
  const service = pickOneBySeed(relatedServices, baseSeed + 3);

  /* =========================================
     RENDER
  ========================================= */
  return (
    <section className="knowledge-hub text-black text-center font-light bg-white leading-relaxed pt-2 pb-20 px-20 mx-auto">
      <h3 className="text-2xl mb-4 text-center">Explore More</h3>
      <ul className="flex flex-wrap justify-center gap-3 underline">

        {blog && (
          <li>
            <Link href={`/blog/${blog}`} prefetch={false} className="hover:text-primary transition"
              aria-label={`Helpful Guide: ${blog.replace(/-/g, ' ')}`}>
              Helpful Guide: {blog.replace(/-/g, ' ')}
            </Link>
          </li>
        )}

        {project && (
          <li>
            <Link href={`/projects/${project}`} prefetch={false} className="hover:text-primary transition"
              aria-label={`Recent Project: ${project.replace(/-/g, ' ')}`}>
              Recent Project: {project.replace(/-/g, ' ')}
            </Link>
          </li>
        )}

        {service && (
          <li>
            <Link href={`/services/${service}`} prefetch={false} className="hover:text-primary transition"
              aria-label={`Our ${service.replace(/-/g, ' ')} Services`}>
              Our {service.replace(/-/g, ' ')} Services
            </Link>
          </li>
        )}

        {podcast && (
          <li>
            <Link href={`/podcast/${podcast}`} prefetch={false} className="hover:text-primary transition"
              aria-label={`Podcast Episode: ${podcast.replace(/-/g, ' ')}`}>
              Podcast Episode: {podcast.replace(/-/g, ' ')}
            </Link>
          </li>
        )}

      </ul>
    </section>
  );
}